/**
 * Values of the client create/update form.
 *
 * Fields backed by a plain input hold a scalar; those backed by a `labelInValue`
 * select hold a { label, value } object. A few are read both ways depending on
 * how the form was populated (fresh input vs. data loaded from the API), which
 * is why their types are unions.
 *
 * Must stay a type alias, not an interface: only aliases get an implicit index
 * signature, which getFormError's `values: Record<string, unknown>` requires.
 */
export interface LocationOption {
  city?: string;
  postalCode?: string;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type ClientFormValues = {
  code?: string;
  name?: string;
  clientType?: string;
  currency?: string;
  comments?: string;
  note?: string;
  chorus?: string;
  euVatNumber?: string;
  paElectronicAddress?: string;
  headquartersNic?: string;
  companyBankAccountId?: ID;
  paymentMethodId?: ID;
  /** Plain string when typed in, { value } when picked from the SIRET lookup. */
  vatNumber?: string | { value?: string };
  paymentDeadline?: { value?: ID };
  legalStructure?: { value?: ID };
  clientVatTypeId?: { value?: ID };
  /** Scalar when the company has no accounting firm, { value } otherwise. */
  counterpartCode?: string | { value?: ID };

  /* Main-address fields, submitted alongside the client on creation. */
  streetName?: string;
  streetNameComplement?: string;
  addressBtq?: string;
  addressNr?: string;
  /** Location selects carry the looked-up record in `data` for cross-filling. */
  postalCode?: { value?: string; data?: LocationOption | null };
  city?: { value?: string; data?: LocationOption | null };
  country?: { value?: ID };
};

/**
 * Reads a field that may hold either a raw scalar or a `labelInValue` select
 * object, returning the underlying scalar.
 */
export function selectValue(field: string | { value?: ID } | undefined): string {
  if (field === undefined || field === null) return "";
  if (typeof field === "object") return field.value === undefined ? "" : String(field.value);
  return field;
}
